import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom, timer } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerChallengeSkillMappingDto, RefreshCareerChallengeFields, RefreshCareerChallengePreviewResponse } from '../../../../models';
import { renderLessonMarkdown } from '../../../../shared/lesson-markdown.util';

@Component({
  selector: 'app-refresh-challenge-content',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatProgressBarModule],
  templateUrl: './refresh-challenge-content.component.html',
  styles: [`
    :host { display: block; margin-bottom: 1rem; }
    .refresh-panel { border: 1px solid #d7dde5; border-radius: 12px; padding: 1.25rem; margin-top: 1rem; }
    .fields, .actions { display: flex; gap: 1rem; flex-wrap: wrap; }
    .comparison { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
    .content { max-height: 28rem; overflow: auto; padding: 1rem; background: #f7f9fb; overflow-wrap: anywhere; }
    .error { color: #b42318; }
    .weekly { display: block; }
    @media (max-width: 700px) { .comparison { grid-template-columns: 1fr; } }
  `]
})
export class RefreshChallengeContentComponent implements OnInit, OnDestroy {
  private destroyed = false;
  private get pendingJobKey(): string { return `challenge-refresh-preview:${this.challengeId}`; }
  ngOnInit(): void { if (sessionStorage.getItem(this.pendingJobKey)) void this.generate(); }
  ngOnDestroy(): void { this.destroyed = true; }
  private readonly api = inject(CsrWebApiService);
  @Input({ required: true }) challengeId = '';
  @Input() disabled = false;
  @Input() disabledReason = '';
  @Output() applied = new EventEmitter<void>();
  preview: RefreshCareerChallengePreviewResponse | null = null;
  fields: RefreshCareerChallengeFields = { coverImage: true, title: true, skills: true, description: true, summary: true, faq: true };
  selectedWeekly: Record<string, boolean> = {};
  acknowledged = false;
  busy = false;
  error = '';
  message = '';
  restoreJobId = '';
  readonly markdown = renderLessonMarkdown;

  get canApply(): boolean {
    return !this.busy && !this.disabled && this.acknowledged && Object.values(this.fields).some(Boolean)
      && !!this.preview && new Date(this.preview.expiresAt).getTime() > Date.now();
  }
  get previewExpired(): boolean { return !!this.preview && new Date(this.preview.expiresAt).getTime() <= Date.now(); }

  get applyDisabledReason(): string {
    if (this.busy) return 'Wait for generation or saving to finish.';
    if (this.disabled) return this.disabledReason || 'Save or discard unsaved challenge edits before applying the refresh.';
    if (!this.preview) return '';
    if (this.previewExpired) return 'This approval expired. Renew it below to use the saved content without another AI call.';
    if (!Object.values(this.fields).some(Boolean)) return 'Select at least one field to refresh.';
    if (!this.acknowledged) return 'Tick the review confirmation checkbox above to enable Apply refresh.';
    return '';
  }

  skillKey(skill: CareerChallengeSkillMappingDto): string {
    return `${skill.skillId}:${(skill.topicName || skill.skillName || '').trim().toLowerCase()}`;
  }

  skillChange(skill: CareerChallengeSkillMappingDto, current: CareerChallengeSkillMappingDto[]): string {
    const previous = current.find(x => this.skillKey(x) === this.skillKey(skill));
    if (!previous) return 'Added';
    return JSON.stringify(previous) === JSON.stringify(skill) ? 'Retained' : 'Updated';
  }

  removedSkills(current: CareerChallengeSkillMappingDto[]): CareerChallengeSkillMappingDto[] {
    return current.filter(x => !this.preview?.content.skills.some(y => this.skillKey(x) === this.skillKey(y)));
  }

  async generate(): Promise<void> {
    if (this.preview) sessionStorage.removeItem(this.pendingJobKey);
    this.busy = true;
    this.error = '';
    this.message = '';
    this.preview = null;
    this.acknowledged = false;
    this.selectedWeekly = {};
    try {
      let jobId = sessionStorage.getItem(this.pendingJobKey);
      if (!jobId) {
        const queued = await firstValueFrom(this.api.queueCareerChallengeRefreshPreview(this.challengeId));
        jobId = queued.jobId;
        sessionStorage.setItem(this.pendingJobKey, jobId);
      }
      this.message = 'Generating content and cover in the background. You can leave this page and return.';
      while (!this.destroyed) {
        const result = await firstValueFrom(this.api.getCareerChallengeRefreshPreviewStatus(this.challengeId, jobId));
        if (result.status === 'expired' && result.preview) {
          this.preview = result.preview;
          this.message = 'Restored the expired comparison. No new AI generation was performed.';
          break;
        }
        if (result.status === 'failed' || result.status === 'expired') {
          sessionStorage.removeItem(this.pendingJobKey);
          throw new Error(result.error || 'Preview generation failed. Generate a new preview to retry.');
        }
        if (result.status === 'ready' && result.preview) {
          this.preview = result.preview;
          this.message = '';
          break;
        }
        await firstValueFrom(timer(2000));
      }
      if (!this.preview) return;
      this.fields.coverImage = !!this.preview.coverImageUrl;
    } catch (error) {
      this.message = '';
      if (error instanceof HttpErrorResponse && error.status === 400) sessionStorage.removeItem(this.pendingJobKey);
      this.error = this.errorMessage(error, 'Could not generate refresh preview. Existing content has been preserved.');
    } finally {
      this.busy = false;
    }
  }

  cancelPreview(): void {
    this.preview = null;
    sessionStorage.removeItem(this.pendingJobKey);
  }

  async restoreSavedPreview(): Promise<void> {
    const jobId = this.restoreJobId.trim();
    if (!jobId || this.busy) return;
    this.preview = null;
    sessionStorage.setItem(this.pendingJobKey, jobId);
    await this.generate();
  }

  async renewPreview(): Promise<void> {
    const jobId = sessionStorage.getItem(this.pendingJobKey);
    if (!jobId || this.busy) return;
    this.busy = true;
    this.error = '';
    this.acknowledged = false;
    this.selectedWeekly = {};
    try {
      this.preview = await firstValueFrom(this.api.renewCareerChallengeRefreshPreview(this.challengeId, jobId));
      this.fields.coverImage = !!this.preview.coverImageUrl;
      this.message = 'Approval renewed for 30 minutes using the saved content and image. Review and confirm before applying.';
    } catch (error) {
      this.error = this.errorMessage(error, 'Could not renew the preview. The challenge may have changed or registration may have closed.');
    } finally { this.busy = false; }
  }

  async apply(): Promise<void> {
    if (!this.preview || !this.canApply) return;
    this.busy = true;
    this.error = '';
    try {
      const result = await firstValueFrom(this.api.applyCareerChallengeRefresh(this.challengeId, {
        token: this.preview.token, fields: this.fields,
        weeklyChallengeIds: Object.keys(this.selectedWeekly).filter(id => this.selectedWeekly[id])
      }));
      const skippedTitles = this.preview.targets.filter(x => result.skipped[x.id]).map(x => x.title);
      this.message = `Refreshed ${result.updatedIds.length} challenge(s).`
        + (skippedTitles.length ? ` Skipped because content changed or registration closed: ${skippedTitles.join(', ')}.` : '');
      this.preview = null;
      sessionStorage.removeItem(this.pendingJobKey);
      this.applied.emit();
    } catch (error) {
      this.error = this.errorMessage(error, 'Could not apply refresh. Reload the challenge before trying again.');
    } finally {
      this.busy = false;
    }
  }

  private errorMessage(error: unknown, fallback: string): string {
    return error instanceof HttpErrorResponse ? (typeof error.error === 'string' ? error.error : fallback)
      : error instanceof Error ? error.message : fallback;
  }
}
